import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { SectionHead, QuoteBand } from "@/components/Section";
import { Reveal, Eyebrow } from "@/components/Motion";
import { ShieldCheck, FileCheck2, Umbrella, FlaskConical, Ban } from "lucide-react";

const ICONS = [ShieldCheck, FileCheck2, Umbrella, FlaskConical, Ban];
const Wrap = ({ children, className = "", ...rest }) => (
  <section className={`py-24 lg:py-32 ${className}`} {...rest}><div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">{children}</div></section>
);

export default function About() {
  const { t } = useLang();
  const a = t.about;
  useSeo("about");
  return (
    <>
      <PageHero eyebrow={a.eyebrow} title={a.title} intro={a.intro} img="/img/yard.jpg" testId="about-hero" />
      <Wrap className="bg-white" data-testid="about-who">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow>{a.whoTitle}</Eyebrow>
            <div className="relative mt-8 aspect-[4/5] overflow-hidden">
              <img src="/img/ship.jpg" alt="Cargo vessel at sea" loading="lazy" className="h-full w-full object-cover" />
              <span className="absolute bottom-0 start-0 h-1 w-24 bg-[#C59B27]" aria-hidden="true" />
            </div>
          </div>
          <div className="space-y-8 lg:col-span-7 lg:pt-16">
            {a.who.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className={i === 0 ? "font-serif text-2xl leading-snug text-[#1E3A5F] sm:text-3xl" : "text-lg leading-relaxed text-[#334155]"}>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Wrap>
      <Wrap className="bg-[#0F2138]" data-testid="about-process">
        <Eyebrow light>{a.processEyebrow}</Eyebrow>
        <h2 className="mt-5 max-w-3xl font-serif text-3xl text-white sm:text-4xl lg:text-5xl">{a.processTitle}</h2>
        <ol className="mt-16 grid gap-px bg-white/15 md:grid-cols-2 lg:grid-cols-4">
          {a.process.map((s, i) => (
            <Reveal as="li" key={s.t} delay={i * 0.1} className="relative bg-[#0F2138] p-8 pt-10" data-testid={`process-step-${i}`}>
              <span className="absolute inset-x-0 top-0 h-px bg-[#C59B27]" style={{ transformOrigin: "left" }} aria-hidden="true" />
              <span className="font-serif text-6xl text-[#C59B27]" dir="ltr">{i + 1}</span>
              <h3 className="mt-6 font-serif text-2xl text-white">{s.t}</h3>
              <p className="mt-3 leading-relaxed text-[#C9D4E1]">{s.d}</p>
            </Reveal>
          ))}
        </ol>
      </Wrap>
      <Wrap className="bg-white" data-testid="about-compliance">
        <SectionHead eyebrow={a.complianceEyebrow} title={a.complianceTitle} />
        <ul className="divide-y divide-[#8EA3BC]/40 border-y border-[#8EA3BC]/40">
          {a.compliance.map((c, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal as="li" key={c.t} delay={i * 0.05} className="grid gap-4 py-8 sm:grid-cols-12 sm:items-center" data-testid={`compliance-item-${i}`}>
                <Icon className="h-6 w-6 text-[#1E3A5F] sm:col-span-1" aria-hidden="true" />
                <h3 className="font-serif text-xl text-[#1E3A5F] sm:col-span-4 sm:text-2xl">{c.t}</h3>
                <p className="text-[#4A5D75] sm:col-span-7">{c.d}</p>
              </Reveal>
            );
          })}
        </ul>
      </Wrap>
      <QuoteBand testId="about-quote-band" />
    </>
  );
}
